import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ChipComponent from "../../../../common/components/chip/ChipComponent";
import AddFeedbackDetailsButton from "./AddFeedbackDetailsButton";
import EditEventFeedbackDetails from "./EditEventFeedbackDetails";
import { useHasRoleAccess } from "../../../../common/hooks/useCheckRoleAccess";

interface EventFeedbackDetailsProps {
  onAdd: (mitigatedRisk: string[] | null) => Promise<void>;
  onEdit: (mitigatedRisk: string[] | null) => Promise<void>;
  feedbacks?: string[];
  loading?: boolean;
  maxVisible?: number;
}

const EventFeedbackDetails = ({
  feedbacks,
  onAdd,
  onEdit,
  loading,
  maxVisible = 2,
}: EventFeedbackDetailsProps) => {
  const hasFeedback = feedbacks && feedbacks.length > 0;
  const visibleFeedbacks = feedbacks?.slice(0, maxVisible);
  const extraCount = (feedbacks?.length ?? 0) - maxVisible;

  const isDeniedAccess = useHasRoleAccess("readonly");

  const showAddFeedbackButton =
    !hasFeedback || (hasFeedback && visibleFeedbacks?.length === 0);

  if (showAddFeedbackButton && !isDeniedAccess) {
    return (
      <AddFeedbackDetailsButton
        loading={loading}
        onSubmitClick={onAdd}
        mitigatedRisk={feedbacks}
      />
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {visibleFeedbacks?.map((item, idx) => (
          <Typography
            key={item + idx}
            variant="body1Regular"
            color="text.primary"
          >
            {item}
          </Typography>
        ))}
      </Box>

      {extraCount > 0 && (
        <Tooltip
          title={
            <ul
              style={{
                margin: 0,
                padding: "2em",
              }}
            >
              {feedbacks?.map((risk: string) => (
                <li key={risk}>
                  <Typography variant="body2Regular">{risk}</Typography>
                </li>
              ))}
            </ul>
          }
          placement="top-start"
          arrow
        >
          <Box>
            <ChipComponent
              label={`+${extraCount}`}
              activeState={false}
              menuBoxStyles={{
                fontSize: "1rem",
                border: theme =>
                  `1px solid ${theme.palette.otherTextTertiary.main}`,
                padding: "4px",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
              textStyles={{
                color: theme => theme.palette.otherTextTertiary.main,
                mr: 0,
                minWidth: "15px",
              }}
              textVariant="caption"
            />
          </Box>
        </Tooltip>
      )}
      {!isDeniedAccess && (
        <EditEventFeedbackDetails
          loading={loading}
          onSubmitClick={onEdit}
          mitigatedRisk={feedbacks}
        />
      )}
    </Stack>
  );
};

export default EventFeedbackDetails;
